import React, { useEffect, useState } from 'react';
import { useApi } from '../auth/useApi';

const C = {
  primary: 'var(--c-primary)', secondary: 'var(--c-secondary)', bg: 'var(--c-bg)',
  surface: 'var(--c-surface)', border: 'var(--c-border)', text: 'var(--c-text)',
  textMuted: 'var(--c-textMuted)', ok: 'var(--c-ok)', warn: 'var(--c-warn)', danger: 'var(--c-danger)',
};

interface Note {
  NoteId: number;
  Body: string;
  CreatedAt: string;
  EditedAt: string | null;
  AuthorName: string;
  AuthorUserId: number;
}

export const ProjectNotes: React.FC<{ projectId: number; canEdit: boolean }> = ({ projectId, canEdit }) => {
  const api = useApi();
  const [notes, setNotes] = useState<Note[]>([]);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const n = await api.get<Note[]>(`/api/projects/${projectId}/notes`); setNotes(Array.isArray(n) ? n : []); }
    catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const add = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try { await api.post(`/api/projects/${projectId}/notes`, { body: body.trim() }); setBody(''); await load(); }
    catch (e: any) { alert(`Failed to add note: ${e?.message ?? 'error'}`); }
    finally { setBusy(false); }
  };

  const fmt = (s: string) => { try { return new Date(s).toLocaleString(); } catch { return s; } };

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 16, marginTop: 16 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: C.textMuted, fontWeight: 600, marginBottom: 12 }}>
        Notes
      </div>

      {loading ? (
        <div style={{ fontSize: 13, color: C.textMuted }}>Loading…</div>
      ) : notes.length === 0 ? (
        <div style={{ fontSize: 13, color: C.textMuted, fontStyle: 'italic', marginBottom: canEdit ? 12 : 0 }}>No notes yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: canEdit ? 12 : 0 }}>
          {notes.map(n => (
            <div key={n.NoteId} style={{ borderLeft: `2px solid ${C.border}`, paddingLeft: 10 }}>
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 2 }}>
                <span style={{ fontWeight: 600, color: C.text }}>{n.AuthorName}</span> · {fmt(n.CreatedAt)}{n.EditedAt ? ' (edited)' : ''}
              </div>
              <div style={{ fontSize: 14, color: C.text, whiteSpace: 'pre-wrap' }}>{n.Body}</div>
            </div>
          ))}
        </div>
      )}

      {canEdit && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Add a note…"
            rows={2}
            style={{ flex: 1, resize: 'vertical', padding: 8, fontSize: 14, borderRadius: 4, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontFamily: 'inherit' }}
          />
          <button
            onClick={add}
            disabled={!body.trim() || busy}
            style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: 4, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: (!body.trim() || busy) ? 'not-allowed' : 'pointer', opacity: (!body.trim() || busy) ? 0.6 : 1 }}
          >
            {busy ? 'Adding…' : 'Add note'}
          </button>
        </div>
      )}
    </div>
  );
};
